import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack } from "@mui/material";
import CustomSelect from "../CustomSelect";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const OrderReport = ({ customers }) => {
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [orderFilters, setOrderFilters] = useState({
    customer: "",
    dateFrom: null,
    dateTo: null,
  });

  const generateOrderReport = async (exportType = 'html') => {
    const params = new URLSearchParams();

    if (orderFilters.customer) {
      params.append('customer', orderFilters.customer);
    }

    if (orderFilters.dateFrom) {
      params.append('dateFrom', orderFilters.dateFrom.format("YYYY-MM-DD"));
    }

    if (orderFilters.dateTo) {
      params.append('dateTo', orderFilters.dateTo.format("YYYY-MM-DD"));
    }

    const queryString = params.toString();

    setLoadingOrder(true);
    try {
      const response = await api.get(
        `/reports/order-report${queryString ? `?${queryString}` : ''}`
      );

      if (response.data && response.data.status) {
        const customerName = orderFilters.customer
          ? customers.find(c => c._id === orderFilters.customer)?.name || "Unknown"
          : "All Customers";
        const dateFromStr = orderFilters.dateFrom ? orderFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = orderFilters.dateTo ? orderFilters.dateTo.format("YYYY-MM-DD") : "All";

        if (exportType === 'excel') {
          const excelData = response.data.report.map(order => ({
            Date: order.date,
            "Order Number": order.orderNumber,
            Paid: order.paid,
            Customer: order.customer,
            Amount: order.amount,
            "Paid Amount": order.paid_amount,
            Balance: order.balance
          }));

          exportToExcel(excelData, `Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        let totalAmount = 0;
        let totalPaidAmount = 0;
        let totalBalance = 0;

        response.data.report.forEach(order => {
          totalAmount += Number(order.amount) || 0;
          totalPaidAmount += Number(order.paid_amount) || 0;
          totalBalance += Number(order.balance) || 0;
        });
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Summary Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
              h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin: 15px; }
              .report-filters { font-size: 14px; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
              .totals-row th {
  background-color: #ffffff !important;
  color: #000;
  font-weight: bold;
}

            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Summary Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${customerName}</p>
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD")}</p>
            </div>
            
            <table>
         <thead>
  <!-- TOP ROW: TOTALS (WHITE BACKGROUND) -->
  <tr class="totals-row">
    <th colspan="4" style="text-align:right;"></th>
    <th>${totalAmount.toFixed(2)}</th>
    <th>${totalPaidAmount.toFixed(2)}</th>
    <th>${totalBalance.toFixed(2)}</th>
  </tr>

  <!-- SECOND ROW: COLUMN HEADERS -->
  <tr>
    <th>Customer</th>
    <th>Date</th>
    <th>Order Number</th>
    <th>Via</th>
    <th>Amount</th>
    <th>Paid Amount</th>
    <th>Balance</th>
  </tr>
</thead>

              <tbody>
        `;

        // Add rows for each order
        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
            <td>${order.customer}</td>
              <td>${order.date}</td>
              <td>${order.orderNumber}</td>
              <td>${order.paid || 'N/A'}</td>
              
              <td>${order.amount}</td>
              <td>${order.paid_amount}</td>
              <td>${order.balance}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;

        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }

        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingOrder(false);
          return;
        }

        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }

        reportWindow.document.write(htmlContent);
        reportWindow.document.close();

        window.addEventListener('message', function (event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'order') {
            generateOrderReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'order') {
            generateOrderReport('excel');
          }
        });

        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected filters");
      }
    } catch (error) {
      console.error("Error generating order report:", error);
      toast.error("Failed to generate order report");
    } finally {
      setLoadingOrder(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      if (orderFilters.customer) params.append('customer', orderFilters.customer);
      if (orderFilters.dateFrom) params.append('dateFrom', orderFilters.dateFrom.format("YYYY-MM-DD"));
      if (orderFilters.dateTo) params.append('dateTo', orderFilters.dateTo.format("YYYY-MM-DD"));

      const queryString = params.toString();

      const response = await api.get(`/reports/order-report${queryString ? `?${queryString}` : ''}`);

      if (response.data && response.data.status) {
        const customerName = orderFilters.customer
          ? customers.find(c => c._id === orderFilters.customer)?.name || "Unknown"
          : "All Customers";
        const dateFromStr = orderFilters.dateFrom ? orderFilters.dateFrom.format("YYYY-MM-DD") : "All";
        const dateToStr = orderFilters.dateTo ? orderFilters.dateTo.format("YYYY-MM-DD") : "All";
        let totalAmount = 0;
        let totalPaidAmount = 0;
        let totalBalance = 0;

        response.data.report.forEach(order => {
          totalAmount += Number(order.amount) || 0;
          totalPaidAmount += Number(order.paid_amount) || 0;
          totalBalance += Number(order.balance) || 0;
        });
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Summary Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { color: #1abc9c; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .totals-row th {
  background-color: #ffffff !important;
  color: #000;
  font-weight: bold;
}
              </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Summary Report</h1>
              <div class="report-filters">
                <p><strong>Customer:</strong> ${customerName}</p>
                <p><strong>Date Range:</strong> ${dateFromStr} to ${dateToStr}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD")}</p>
            </div>
            
            <table>
   <thead>
  <!-- TOP ROW: TOTALS (WHITE BACKGROUND) -->
  <tr class="totals-row">
    <th colspan="4" style="text-align:right;"></th>
    <th>${totalAmount.toFixed(2)}</th>
    <th>${totalPaidAmount.toFixed(2)}</th>
    <th>${totalBalance.toFixed(2)}</th>
  </tr>

  <!-- SECOND ROW: COLUMN HEADERS -->
  <tr>
    <th>Customer</th>
    <th>Date</th>
    <th>Order Number</th>
    <th>Via</th>
    <th>Amount</th>
    <th>Paid Amount</th>
    <th>Balance</th>
  </tr>
</thead>
              <tbody>
        `;

        // Add rows for each order
        response.data.report.forEach(order => {
          htmlContent += `
            <tr>
            <td>${order.customer}</td>
              <td>${order.date}</td>
              <td>${order.orderNumber}</td>
              <td>${order.paid || 'N/A'}</td>
              
              <td>${order.amount}</td>
              <td>${order.paid_amount}</td>
              <td>${order.balance}</td>
            </tr>
          `;
        });

        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;

        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);

        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Order_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected filters');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={4}>
        <CustomSelect
          label="Customer"
          value={orderFilters.customer}
          onChange={(e) => setOrderFilters({ ...orderFilters, customer: e.target.value })}
          options={[
            { id: "", name: "All Customers" },
            ...customers.map((customer) => ({
              id: customer._id,
              name: customer.name,
            })),
          ]}
          fullWidth
          sx={{ minWidth: "100%" }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="From Date"
          value={orderFilters.dateFrom}
          onChange={(newValue) => setOrderFilters({ ...orderFilters, dateFrom: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <DatePicker
          label="To Date"
          value={orderFilters.dateTo}
          onChange={(newValue) => setOrderFilters({ ...orderFilters, dateTo: newValue })}
          slotProps={{ textField: { fullWidth: true } }}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateOrderReport('html')}
            disabled={loadingOrder || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{
              height: "38px",
              backgroundColor: colors.primary,
              '&:hover': {
                backgroundColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            {loadingOrder ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateOrderReport('pdf')}
            disabled={loadingOrder || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateOrderReport('excel')}
            disabled={loadingOrder || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateOrderReport('email')}
            disabled={loadingOrder || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': {
                borderColor: colors.primary,
                opacity: 0.9
              }
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default OrderReport;
